// src/MathTools/SystemSolverTool.js
import React, { useState } from "react";

/**
 * SystemSolverTool
 *
 * Props:
 *   initA1, initB1, initC1, initA2, initB2, initC2: numbers (initial coefficients)
 *   showGraph: boolean — whether to draw the SVG graph
 *   width, height: number — size of the SVG
 *   padding: number — margin around the axes inside SVG
 *   showSolution: boolean — whether to display the numeric “Solution:” text
 *   showInputs: boolean — whether to show the input fields for a, b, c
 */
function solveLinear(a1, b1, c1, a2, b2, c2) {
  const D = a1 * b2 - a2 * b1;
  if (D === 0) return null; // no unique solution
  return {
    x: (c1 * b2 - c2 * b1) / D,
    y: (a1 * c2 - a2 * c1) / D,
  };
}

function lineY(a, b, c, x) {
  // solve: a x + b y = c  ⇒  y = (c − a x)/b
  return (c - a * x) / b;
}

export default function SystemSolverTool({
  initA1 = 1,
  initB1 = 1,
  initC1 = 6,
  initA2 = 1,
  initB2 = -1,
  initC2 = 2,
  showGraph = true,
  width = 450,
  height = 450,
  padding = 30,
  showSolution = true,
  showInputs = true,
}) {
  const [a1, setA1] = useState(initA1);
  const [b1, setB1] = useState(initB1);
  const [c1, setC1] = useState(initC1);
  const [a2, setA2] = useState(initA2);
  const [b2, setB2] = useState(initB2);
  const [c2, setC2] = useState(initC2);

  const solution = solveLinear(a1, b1, c1, a2, b2, c2);

  // coordinate system bounds
  const xMin = -10,
    xMax = 10,
    yMin = -10,
    yMax = 10;

  // scales
  const scaleX = (width - 2 * padding) / (xMax - xMin);
  const scaleY = (height - 2 * padding) / (yMax - yMin);

  // conversion from math coords to SVG
  const toSvgX = (x) => padding + (x - xMin) * scaleX;
  const toSvgY = (y) => height - (padding + (y - yMin) * scaleY);

  function makeLine(a, b, c, color) {
    // special case: vertical line (b=0 => x=c/a)
    if (b === 0) {
      const x = c / a;
      const svgX = toSvgX(x);
      return (
        <line
          key={color}
          x1={svgX}
          y1={toSvgY(yMin)}
          x2={svgX}
          y2={toSvgY(yMax)}
          stroke={color}
          strokeWidth={2}
        />
      );
    }

    // otherwise plot 2 points from the extremes of x
    const pts = [];
    for (let i = 0; i <= 1; i++) {
      const xVal = xMin + i * (xMax - xMin);
      const yVal = lineY(a, b, c, xVal);
      pts.push([toSvgX(xVal), toSvgY(yVal)]);
    }
    return (
      <polyline
        key={color}
        points={pts.map((p) => p.join(",")).join(" ")}
        stroke={color}
        strokeWidth={2}
        fill="none"
      />
    );
  }

  const NumInput = ({ label, value, setter }) => (
    <label style={{ marginRight: 12 }}>
      {label}:{" "}
      <input
        type="number"
        value={value}
        onChange={(e) => setter(parseFloat(e.target.value) || 0)}
        style={{ width: 50 }}
      />
    </label>
  );

  return (
    <div style={{ border: "1px solid #ccc", padding: 12, borderRadius: 6 }}>
      <h4>2×2 System Solver</h4>

      {/* Conditionally render the input fields */}
      {showInputs && (
        <div>
          <p><em>Equation 1:</em></p>
          <NumInput label="a₁" value={a1} setter={setA1} />
          <NumInput label="b₁" value={b1} setter={setB1} />
          <NumInput label="c₁" value={c1} setter={setC1} />

          <p><em>Equation 2:</em></p>
          <NumInput label="a₂" value={a2} setter={setA2} />
          <NumInput label="b₂" value={b2} setter={setB2} />
          <NumInput label="c₂" value={c2} setter={setC2} />
        </div>
      )}

      {/* Conditionally render the solution text */}
      {showSolution && (
        <div style={{ margin: "8px 0" }}>
          <strong>Solution:</strong>{" "}
          {solution ? (
            <span>
              x = {solution.x.toFixed(3)}, y = {solution.y.toFixed(3)}
            </span>
          ) : (
            <span style={{ color: "red" }}>No unique solution</span>
          )}
        </div>
      )}

      {showGraph && (
        <>
          <svg
            width={width}
            height={height}
            style={{ border: "1px solid #ddd", background: "#fafafa" }}
          >
            {/* 1) x-axis */}
            <line
              x1={toSvgX(xMin)}
              y1={toSvgY(0)}
              x2={toSvgX(xMax)}
              y2={toSvgY(0)}
              stroke="#888"
              strokeWidth={2}
            />
            {/* 2) y-axis */}
            <line
              x1={toSvgX(0)}
              y1={toSvgY(yMin)}
              x2={toSvgX(0)}
              y2={toSvgY(yMax)}
              stroke="#888"
              strokeWidth={2}
            />

            {/* 3) Tick marks & labels */}
            <g fontSize="10" fill="#444" stroke="#444" strokeWidth={1}>
              {/* X-axis ticks */}
              {Array.from({ length: xMax - xMin + 1 }, (_, i) => xMin + i).map(
                (xVal) => {
                  if (!Number.isInteger(xVal)) return null; // only integer ticks
                  const xPix = toSvgX(xVal);
                  const yZeroPix = toSvgY(0);
                  return (
                    <g key={`xTick-${xVal}`}>
                      {/* Tick line */}
                      <line
                        x1={xPix}
                        y1={yZeroPix - 5}
                        x2={xPix}
                        y2={yZeroPix + 5}
                      />
                      {/* Numeric label below the axis */}
                      <text x={xPix - 5} y={yZeroPix + 18}>
                        {xVal}
                      </text>
                    </g>
                  );
                }
              )}

              {/* Y-axis ticks */}
              {Array.from({ length: yMax - yMin + 1 }, (_, i) => yMin + i).map(
                (yVal) => {
                  if (!Number.isInteger(yVal)) return null;
                  const yPix = toSvgY(yVal);
                  const xZeroPix = toSvgX(0);
                  return (
                    <g key={`yTick-${yVal}`}>
                      {/* Tick line */}
                      <line
                        x1={xZeroPix - 5}
                        y1={yPix}
                        x2={xZeroPix + 5}
                        y2={yPix}
                      />
                      {/* Numeric label to right of y-axis */}
                      <text x={xZeroPix + 8} y={yPix + 4}>
                        {yVal}
                      </text>
                    </g>
                  );
                }
              )}
            </g>

            {/* 4) The two lines (Eqn1 & Eqn2) */}
            {makeLine(a1, b1, c1, "#d32f2f")}
            {makeLine(a2, b2, c2, "#1976d2")}
          </svg>

          <p style={{ fontSize: 14, marginTop: 4 }}>
            <span style={{ color: "#d32f2f" }}>■ Eqn 1</span>
            <span style={{ marginLeft: 16, color: "#1976d2" }}>■ Eqn 2</span>
          </p>
        </>
      )}
    </div>
  );
}
